import { useState, useCallback, useMemo } from 'react';
import { startOfWeek, addDays, subDays, addWeeks, subWeeks } from 'date-fns';
import { getWeekDays } from '../components/Calendar/utils/getWeekDays';

export enum CalendarView {
  Dia = 0,
  Semana = 1,
  Mes = 2,
}

export interface ViewOption {
  label: string;
  value: CalendarView;
}

interface UseCalendarNavigationReturn {
  currentDate: Date;
  view: CalendarView;
  setView: (view: CalendarView) => void;
  viewsOptions: ViewOption[];
  handleViewChange: (value: string | number | null) => void;
  goToToday: () => void;
  navigate: (direction: 'prev' | 'next') => void;
  goToDate: (date: Date) => void;
  weekDays: Date[];
}

export const useCalendarNavigation = (
  initialDate?: Date,
  initialView: CalendarView = CalendarView.Semana,
): UseCalendarNavigationReturn => {
  const [currentDate, setCurrentDate] = useState<Date>(initialDate || startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [view, setView] = useState<CalendarView>(initialView);

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  const viewsOptions: ViewOption[] = useMemo(() => [{ label: 'Semana', value: CalendarView.Semana }], []);

  const goToToday = useCallback(() => {
    setCurrentDate(startOfWeek(new Date(), { weekStartsOn: 0 }));
  }, []);

  const navigate = useCallback(
    (direction: 'prev' | 'next') => {
      setCurrentDate(prev => {
        if (view === CalendarView.Semana) {
          const newDate = direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1);
          return startOfWeek(newDate, { weekStartsOn: 0 });
        } else if (view === CalendarView.Dia) {
          return direction === 'next' ? addDays(prev, 1) : subDays(prev, 1);
        } else {
          return direction === 'next' ? addDays(prev, 1) : subDays(prev, 1);
        }
      });
    },
    [view],
  );

  const goToDate = useCallback((date: Date) => {
    setCurrentDate(startOfWeek(date, { weekStartsOn: 0 }));
  }, []);

  const handleViewChange = useCallback(
    (value: string | number | null) => {
      if (value !== null && Object.values(CalendarView).includes(value as CalendarView)) {
        setView(value as CalendarView);
      }
    },
    [setView],
  );

  return {
    currentDate,
    view,
    setView,
    viewsOptions,
    handleViewChange,
    goToToday,
    navigate,
    goToDate,
    weekDays,
  };
};
